import * as Actions from '../actions/participantActions';
import Participant from '../../entities/participant';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type ParticipantAction = { 
    type: string; 
    participant: Participant | null; 
    participantId: string | Participant; 
    partial: boolean; 
    lastFetch: number; 
    participants: Participant[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Participant> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Participant[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Participant> | null, firmId: string, userId: string, updates?: Participant[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'participant', 'participants', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Participant>} data
 * @param {Participant[]} [updates]
 * @returns {ArrayMap<Participant>}
 */
function updateStorage(data: ArrayMap<Participant>, updates?: Participant[]): ArrayMap<Participant> {
	if (logging) console.debug('⮂ participantReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Participant[]} [updates]
 * @returns {ArrayMap<Participant>}
 */
function updateStorageNoReturn(updates?: Participant[]): void {
	if (logging) console.debug('⮂ participantReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Participant[]} [data]
 * @returns {ArrayMap<Participant>}
 */
function createState(data?: Participant[] | ArrayMap<Participant>): ArrayMap<Participant> {
	if (!data) return ArrayMapRegistry.clone<Participant>('participant', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Participant>('participant', data.map(e => e));
    else return ArrayMapRegistry.clone<Participant>('participant', data);
}

function participants(state: ArrayMap<Participant>, action: ParticipantAction): ArrayMap<Participant> {
	if (!state) state = createState();

	let st: ArrayMap<Participant>;

	switch (action.type) {
		// action.participant - the new value of participant with id action.id
		// action.participantId - id of the target, used if new value is null
		case Actions.PARTICIPANT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ participantReducer: FETCH_ONE_SUCCESS', action);

			if (action.participant === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.participantId)) {
				st = state.clone();
				st.set(action.participant);
				// state.clear();

				updateStorageNoReturn([action.participant]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.participants
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.participants
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.participants
			 !partial, !stateEmpty, fetchEmpty	-> D return participants in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge participants in state newer than those in action.participants
		 */
		case Actions.PARTICIPANT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.participants.length === 0;
			const fetched = action.participants.map(participant => new Participant({...participant, persisted: true}));

			if (logging) console.debug(`⮂ participantReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} participants with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ participantReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ participantReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Participant> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Participant[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(participant => {
					const current = st.get(participant.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && participant.status === 'D') {
							// Remove it from state
							toUpdate.push(participant);
							st.delete(participant);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (participant.addedOn > current.addedOn) {
								toUpdate.push(participant);
								st.set(participant);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (participant.status !== 'D') {
							toUpdate.push(participant);
							st.set(participant);
						}
						else if (logging) console.debug('⮂ participantReducer: partialMerge: skipping deleted participant not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ participantReducer: empty: participants', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ participantReducer: fullMerge: participants', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localParticipant => {
                    // If this participant has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localParticipant.persisted) {
                        const fetchedParticipant = st.get(localParticipant.id);
                        if (!fetchedParticipant || localParticipant.addedOn > fetchedParticipant.addedOn) st.set(localParticipant);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.PARTICIPANT_FETCH_ONE_FAILURE:
			console.error('PARTICIPANT_FETCH_ONE_FAILURE', action.participantId, action.err);

			// No update to storage required
			return state;

		case Actions.PARTICIPANT_FETCH_FAILURE:
			console.error('PARTICIPANT_FETCH_FAILURE', action.participantId, action.err);

			// No update to storage required
			return state;

		case Actions.PARTICIPANT_RESTORE: {
		    let participant = new Participant({...action.participant});
            st = state.clone();

            const existing = st.get(participant.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (participant.addedOn >= existing.addedOn) {
                    st.set(participant);
                    return updateStorage(st, [participant]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (participant.status === 'D') return state;

                st.set(participant);
                return updateStorage(st, [participant]);
            }
        }
            
		case Actions.PARTICIPANT_SAVE: {
		    // console.debug('⮂ PARTICIPANT_SAVE reducer', action.participant?.id);
		    
			// clone the participant
			let participant = new Participant({...action.participant});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new participant in the state
			// If this is a create, add to the queue
			const _participant = st.get(participant.id);

			if (!_participant) st.set(participant);
			else {
				if (participant.addedOn >= _participant.addedOn) st.set(participant);
			}

			return updateStorage(st, [participant]);
		}

		case Actions.PARTICIPANT_SAVE_MANY: {
		    if (logging) console.debug('⮂ PARTICIPANT_SAVE_MANY reducer', action.participants.map(participant => participant.id).join(','));
		    
			// clone the participants
			let toProcess = action.participants.map(participant => new Participant(participant));

			st = state.clone();
			// state.clear();

			const updates: Participant[] = [];

			toProcess.forEach(participant => {
				// Add or update the new participant in the state
				// If this is a create, add to the queue
				const existing = st.get(participant.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (participant.status !== 'D') {
						st.push(participant);
						updates.push(participant);
					}
					else if (logging) console.debug('⮂ PARTICIPANT_SAVE_MANY: skipping deleted participant not in state');
				}
				else {
					if (participant.addedOn >= existing.addedOn) {
						st.set(participant);
						updates.push(participant);
					}
					else if (logging) console.debug('⮂ Skipping, existing participant had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.PARTICIPANT_REMOVE: {
			// clone the participant
			const participant = new Participant({...action.participant});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(participant);

			return updateStorage(st, [participant]);
		}

		case Actions.PARTICIPANT_RESTORE_FAILURE:
		case Actions.PARTICIPANT_SAVE_FAILURE: {
		    console.warn('PARTICIPANT_SAVE_FAILURE reducer', action.participant?.id);
		    if (!action.participant?.id) {
				console.warn('PARTICIPANT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const participant = ensure(Participant, action.participant);
			if (!participant) {
				console.error('Could not find participant after call to save');
				return state;
			}

			st = state.clone();

			const _participantRef = state.get(participant.id);

			if (_participantRef && (_participantRef.addedOn === participant.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _participant = new Participant({..._participantRef});
				_participant.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_participant.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('participant', _participant).then(r => {
						if (r.isErr()) console.error(`⮂ participantReducer: failed to save participant with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ participantReducer: saved participant with fatalError info');
					}).catch(err => console.error(`⮂ participantReducer: failed to save participant with fatalError info: ${err.message}`));

				st.set(_participant);
			}

			return updateStorage(st);
		}

		case Actions.PARTICIPANT_SAVE_MANY_FAILURE: {
		    console.warn('PARTICIPANT_SAVE_MANY_FAILURE reducer', action.participants.map(participant => participant.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.PARTICIPANT_RESTORE_SUCCESS:
		case Actions.PARTICIPANT_SAVE_SUCCESS: {
			if (!action.participant?.id) {
				console.warn('PARTICIPANT_SAVE_SUCCESS reducer');
                return state;
			}

			const participantRef = state.get(action.participant?.id);
			if (!participantRef) {
				console.warn('Could not find participant after call to save');
                return state;
			}

			st = state.clone();

			if (action.participant.addedOn >= participantRef.addedOn) {
				st.set(action.participant);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const participant = new Participant({...participantRef, persisted: true});
				st.set(participant);
			}

			return updateStorage(st, [action.participant]);
		}

		case Actions.PARTICIPANT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ PARTICIPANT_SAVE_MANY_SUCCESS reducer', action.participants.map(participant => participant.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const participants: Participant[] = [];

			action.participants.forEach(e => {
				const participantRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!participantRef) console.warn('Could not find participant after call to save');
				else if (e.addedOn >= participantRef.addedOn) {
				    st.set(e);
				    participants.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const participant = new Participant({...participantRef, persisted: true});
					st.set(participant);
					participants.push(participant);
				}
			});

			return updateStorage(st, participants);
		}

		case Actions.PARTICIPANT_REMOVE_FAILURE: {
		    console.warn('PARTICIPANT_REMOVE_FAILURE action', action.participant?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.PARTICIPANT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.PARTICIPANT_RESTORE_MANY: {
			if (logging) console.debug('⮂ PARTICIPANT_RESTORE_MANY reducer', action.participants.map(participant => participant.id).join(','));

			const toProcess = action.participants.map(participant => new Participant({...participant}));
			st = state.clone();

			const updates: Participant[] = [];

			toProcess.forEach(participant => {
				const existing = st.get(participant.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (participant.addedOn >= existing.addedOn) {
						st.set(participant);
						updates.push(participant);
					}
					else if (logging) console.debug('⮂ PARTICIPANT_RESTORE_MANY: skipping older version of participant');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (participant.status !== 'D') {
						st.set(participant);
						updates.push(participant);
					}
					else if (logging) console.debug('⮂ PARTICIPANT_RESTORE_MANY: skipping deleted participant not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.PARTICIPANT_REMOVE_MANY: {
			if (logging) console.debug('⮂ PARTICIPANT_REMOVE_MANY reducer', action.participants.map(participant => participant.id).join(','));

			const toProcess = action.participants.map(participant => new Participant({...participant}));
			st = state.clone();

			toProcess.forEach(participant => {
				st.delete(participant);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default participants;